<template>
  <FormLayout
    :key="$route.path"
    :title="title"
    :is-saving="isSaving"
    :is-loading="!resource"
    :has-changes="hasChanges"
    :label="archiveLabel"
    @on-save="submit"
    @on-dismiss="dismiss"
  >
    <vs-form v-if="resource" ref="form">
      <vs-alert v-if="isUpdateError" type="error" data-test="vs-alert-MAD-resources">
        {{ updateError.message }}
      </vs-alert>

      <v-spacer />

      <vs-text-input
        :value="formData.name"
        label="Name"
        required
        data-test="vs-text-input-resource-name"
        @input="(e) => handleUpdateForm('name', e)"
      />

      <vs-text-input
        :value="formData.description"
        label="Description"
        multiline
        data-test="vs-text-input-resource-description"
        @input="(e) => handleUpdateForm('description', e)"
      />

      <vs-text-input
        :value="formData.statusDescription"
        label="Ticker Text"
        hint="Appears under the resource in schedule view."
        :max-length="32"
        data-test="vs-text-input-resource-ticker-text"
        @input="(e) => handleUpdateForm('statusDescription', e)"
      />

      <v-spacer />

      <vs-wrapper direction="horizontal" space="small">
        <vs-autocomplete
          v-if="resourceTypeOptions"
          :value="formData.resourceTypeId"
          label="Resource Type"
          :items="resourceTypeOptions"
          required
          :disabled="!canFullyModifyResource"
          data-test="vs-autocomplete-resourcetype"
          @input="(e) => handleUpdateForm('resourceTypeId', e)"
        />

        <vs-autocomplete
          v-if="resourceOwnerOptions"
          :value="formData.ownerId"
          label="Owner"
          :items="resourceOwnerOptions"
          required
          :disabled="!canFullyModifyResource"
          data-test="vs-autocomplete-resource-owner"
          @input="(e) => handleUpdateForm('ownerId', e)"
        />
      </vs-wrapper>

      <vs-wrapper direction="horizontal" space="small">
        <vs-autocomplete
          v-if="formData.ownerId && formData.ownerId != '' && divisionOptions"
          :value="formData.divisionId"
          label="Division"
          :items="divisionOptions"
          data-test="vs-autocomplete-resource-division"
          @input="(e) => handleUpdateForm('divisionId', e)"
        />

        <vs-autocomplete
          v-if="resource && resource.id"
          :value="status"
          :items="statusItems"
          label="Status"
          required
          data-test="vs-autocomplete-resource-status"
          @input="(e) => handleUpdateForm('status', e)"
        />
      </vs-wrapper>
    </vs-form>

    <template v-if="canFullyModifyResource" #action-bar>
      <TwoStageButton
        v-if="resource && resource.id"
        :alternate="formData.isArchived ?? false"
        :is-loading="isArchiving || isUnarchiving"
        alternate-button-text="Unarchive resource"
        main-button-text="Archive resource"
        @click="handleArchive"
      />
    </template>
  </FormLayout>
</template>

<script setup lang="ts">
import FormLayout from '@/components/common/FormLayout.vue'
import { useStore } from 'vuex-composition-helpers'
import { computed, reactive, ref, watchEffect } from 'vue'
import { OFFLINE, STANDBY } from '@/models/unit_number'
import { useResourceQuery } from '@/api/unit_numbers'
import { useRouter } from 'vue-router/composables'
import VsForm from '@/components/vision/VsForm.vue'
import {
  useArchiveUnitNumberMutation,
  useUnarchiveUnitNumberMutation,
  useUpdateUnitNumberMutation,
} from '@/api/account-data/manage-unit-numbers.js'
import TwoStageButton from '@/components/common/TwoStageButton.vue'
import {
  useGetDivisionOptions,
  useGetResourceOwnerOptions,
  useGetResourceTypeOptions,
} from '@/api/options'
import { useQueryClient } from '@tanstack/vue-query'

const props = withDefaults(
  defineProps<{
    id: number | string
    dismissTo?: { name: string } | null
  }>(),
  {
    dismissTo: null,
  },
)
interface FormData {
  name: string | null
  description: string | null
  statusDescription: string | null
  resourceTypeId: number | null
  ownerId: string | null
  divisionId: number | null
  status: number | null
  isArchived: boolean | null
}

const store = useStore()
const router = useRouter()
const emit = defineEmits(['dismiss', 'save'])

const queryClient = useQueryClient()

const isSaving = ref(false)
const form = ref<InstanceType<typeof VsForm>>()
const formData = reactive<FormData>({
  name: null,
  description: null,
  statusDescription: null,
  resourceTypeId: null,
  ownerId: null,
  divisionId: null,
  status: null,
  isArchived: null,
})

const idRef = computed(() => Number(props.id))
const { data: resource, isLoading, refetch } = useResourceQuery(idRef)

const { data: resourceTypeOptions } = useGetResourceTypeOptions()
const { data: resourceOwnerOptions } = useGetResourceOwnerOptions()
const { data: divisionOptions } = useGetDivisionOptions()

const {
  error: updateError,
  isError: isUpdateError,
  mutateAsync: updateResource,
} = useUpdateUnitNumberMutation()
const { isLoading: isArchiving, mutateAsync: archiveResource } = useArchiveUnitNumberMutation()
const { isLoading: isUnarchiving, mutateAsync: unarchiveResource } =
  useUnarchiveUnitNumberMutation()

watchEffect(() => {
  if (!isLoading.value && resource.value) {
    // Initial loading - Only run once
    // isLoading will only change from true to false once, at the beginning
    formData.name = resource.value.name
    formData.description = resource.value.description
    formData.statusDescription = resource.value.statusDescription
    formData.resourceTypeId = resource.value.resourceTypeId
    formData.ownerId = resource.value.ownerId
    formData.divisionId = resource.value.divisionId
    formData.status = resource.value.status
    formData.isArchived = !resource.value.isVisible
  }
})

const hasChanges = computed(() => {
  return (
    formData.name !== resource.value?.name ||
    formData.description !== resource.value?.description ||
    formData.statusDescription !== resource.value?.statusDescription ||
    formData.resourceTypeId !== resource.value?.resourceTypeId ||
    formData.ownerId !== resource.value?.ownerId ||
    formData.divisionId !== resource.value?.divisionId ||
    formData.status !== resource.value?.status
  )
})

const title = computed(() => {
  if (resource.value && resource.value.id) {
    return `Edit ${resource.value.name}`
  }
  return null
})

const statusItems = ref([
  { text: 'Online', value: STANDBY },
  { text: 'Offline', value: OFFLINE },
])
const status = computed(() => (formData.status === OFFLINE ? OFFLINE : STANDBY))
const archiveLabel = computed(() => (formData.isArchived ? 'Archived' : ''))

// Vuex
const canFullyModifyResource = computed<boolean>(
  () => store.getters['permissions/canFullyModifyResource'],
)

function handleUpdateForm(field: string, e: any) {
  if (field === 'statusDescription') {
    formData[field] = e || ''
    return
  }
  formData[field] = e
}

function dismiss() {
  if (hasChanges.value) {
    const confirmation = window.confirm(
      'There are unsaved changes, are you sure you want to discard them?',
    )

    if (!confirmation) {
      return
    }
  }

  if (props.dismissTo) {
    router.push(props.dismissTo)
  } else {
    emit('dismiss')
  }
}

async function submit() {
  isSaving.value = true
  form.value?.isValid() && (await update())
  isSaving.value = false
}

async function update() {
  try {
    await updateResource({
      id: props.id,
      resourceTypeId: formData.resourceTypeId,
      name: formData.name,
      divisionId: formData.divisionId,
      status: formData.status,
      statusDescription: formData.statusDescription,
      description: formData.description,
      isVisible: !formData.isArchived,
      ownerId: formData.ownerId,
    })

    queryClient.invalidateQueries(['resource', props.id])
  } catch (e) {
    return
  }

  if (!isUpdateError.value) {
    await refetch()
    emit('save')
  }
}

async function handleArchive() {
  const confirmation = window.confirm('Are you sure you want to archive this resource?')
  if (confirmation && resource.value) {
    try {
      if (formData.isArchived) {
        await unarchiveResource(resource.value)
      } else {
        await archiveResource(resource.value)
      }
      formData.isArchived = !formData.isArchived
      await refetch()
      emit('save')
    } catch (e) {}
  }
}
</script>
